import {
  collectUtmParams,
  loadWebformConfig,
  resolveApiBase,
  submitVobWebform,
} from './api';
import {
  isEmbedTestPage,
  readClientIdFromSearch,
  readFacilityIdFromSearch,
} from './embed-query-params';
import type { VobWebformRuntimeConfig } from './types';
import { WEBFORM_STYLES } from './styles';
import type {
  LeadWebformAttributes,
  LeadWebformThemeAttributes,
  VobWebformConfig,
  VobWebformFieldKey,
  VobWebformPayor,
  VobWebformResult,
  VobWebformSubmitPayload,
  VobWebformThemeAttributes,
} from './types';

const THEME_CSS_VARS: Record<keyof VobWebformThemeAttributes, string> = {
  primaryColor: '--fp-vob-primary-color',
  backgroundColor: '--fp-vob-background-color',
  textColor: '--fp-vob-text-color',
  placeholderColor: '--fp-vob-input-placeholder',
  inputColor: '--fp-vob-input-color',
  inputBackgroundColor: '--fp-vob-input-background-color',
  elementBorderColor: '--fp-vob-element-border-color',
};

const DEFAULT_THEME: VobWebformThemeAttributes = {
  primaryColor: '#f5650f',
  backgroundColor: '#ffffff',
  textColor: '#363636',
  placeholderColor: '#949494',
  inputColor: '#000000',
  inputBackgroundColor: '#ffffff',
  elementBorderColor: '#ffffff',
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export class FinpayVobWebformElement extends HTMLElement {
  private apiBase = '';
  private clientId = 0;
  private facilityId = 0;
  private apiToken: string | null = null;
  private webformConfig: VobWebformConfig = { payors: [] };
  private client: VobWebformSubmitPayload = {};
  private payorResults: VobWebformPayor[] = [];
  private loading = true;
  private result: VobWebformResult | undefined;
  private theme: VobWebformThemeAttributes = { ...DEFAULT_THEME };

  static get observedAttributes(): string[] {
    return [
      'client-id',
      'facility-id',
      'instance',
      'api-token',
      'send-to',
      'email-template-id',
      'redirect-on-success-url',
      'queue',
      'primary-color',
      'background-color',
      'text-color',
      'placeholder-color',
      'input-color',
      'input-background-color',
      'element-border-color',
    ];
  }

  connectedCallback(): void {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }
    this.readRuntimeAttributes();
    this.readThemeFromAttributes();
    this.applyThemeToHost();
    void this.loadConfig();
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'client-id' || name === 'facility-id' || name === 'instance' || name === 'api-token') {
      this.readRuntimeAttributes();
      if (this.isConnected) {
        void this.loadConfig();
      }
      return;
    }

    const themeKey = this.attributeToThemeKey(name);
    if (themeKey) {
      this.theme[themeKey] = newValue || DEFAULT_THEME[themeKey];
      this.applyThemeToHost();
      this.render();
    }
  }

  private attributeToThemeKey(
    attr: string
  ): keyof VobWebformThemeAttributes | null {
    const map: Record<string, keyof VobWebformThemeAttributes> = {
      'primary-color': 'primaryColor',
      'background-color': 'backgroundColor',
      'text-color': 'textColor',
      'placeholder-color': 'placeholderColor',
      'input-color': 'inputColor',
      'input-background-color': 'inputBackgroundColor',
      'element-border-color': 'elementBorderColor',
    };
    return map[attr] ?? null;
  }

  private readFacilityIdAttribute(): number {
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    const fromUrl = readFacilityIdFromSearch();

    if (isEmbedTestPage(pathname) && fromUrl != null) {
      return fromUrl;
    }

    const raw =
      this.getAttribute('facility-id') ??
      this.getAttribute('facilityid') ??
      this.getAttribute('facility_id');
    const fromAttr = Number(raw ?? 0);
    if (fromAttr > 0) {
      return fromAttr;
    }
    return fromUrl ?? 0;
  }

  private readClientIdAttribute(): number {
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    const fromUrl = readClientIdFromSearch();

    if (isEmbedTestPage(pathname) && fromUrl != null) {
      return fromUrl;
    }

    const fromAttr = Number(this.getAttribute('client-id') ?? 0);
    if (fromAttr > 0) {
      return fromAttr;
    }
    return fromUrl ?? 0;
  }

  private readRuntimeAttributes(): void {
    this.apiBase = resolveApiBase(this.getAttribute('instance'));
    this.clientId = this.readClientIdAttribute();
    this.facilityId = this.readFacilityIdAttribute();
    this.apiToken = this.getAttribute('api-token');
  }

  private readQueueAttribute(): boolean {
    const raw = this.getAttribute('queue');
    if (raw === null) {
      return true;
    }
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'false' || normalized === '0' || normalized === 'no') {
      return false;
    }
    return true;
  }

  private readPopulatedEmbedAttributes(): LeadWebformAttributes {
    const attributes: LeadWebformAttributes = {
      queue: this.readQueueAttribute(),
    };

    const instance = this.getAttribute('instance')?.trim();
    if (instance) {
      attributes.instance = instance;
    }

    const sendTo = this.readSendToAttribute();
    if (sendTo) {
      attributes.sendTo = sendTo;
    }

    const emailTemplateId = this.readEmailTemplateIdAttribute();
    if (emailTemplateId) {
      attributes.emailTemplateId = emailTemplateId;
    }

    const redirectOnSuccessUrl = this.readRedirectOnSuccessUrlAttribute();
    if (redirectOnSuccessUrl) {
      attributes.redirectOnSuccessUrl = redirectOnSuccessUrl;
    }

    const apiToken = this.getAttribute('api-token')?.trim();
    if (apiToken) {
      attributes.apiToken = apiToken;
    }

    const themeAttrMap: Record<string, keyof LeadWebformThemeAttributes> = {
      'primary-color': 'primaryColor',
      'background-color': 'backgroundColor',
      'text-color': 'textColor',
      'placeholder-color': 'placeholderColor',
      'input-color': 'inputColor',
      'input-background-color': 'inputBackgroundColor',
      'element-border-color': 'elementBorderColor',
    };

    const theme: LeadWebformThemeAttributes = {};
    let hasTheme = false;
    Object.entries(themeAttrMap).forEach(([attr, key]) => {
      const value = this.getAttribute(attr)?.trim();
      if (value) {
        theme[key] = value;
        hasTheme = true;
      }
    });
    if (hasTheme) {
      attributes.theme = theme;
    }

    return attributes;
  }

  private readSendToAttribute(): string | undefined {
    const raw =
      this.getAttribute('send-to') ??
      this.getAttribute('sendto') ??
      this.getAttribute('send_to');
    const trimmed = raw?.trim();
    return trimmed || undefined;
  }

  private readEmailTemplateIdAttribute(): string | undefined {
    const raw =
      this.getAttribute('email-template-id') ??
      this.getAttribute('emailtemplateid') ??
      this.getAttribute('email_template_id');
    const trimmed = raw?.trim();
    return trimmed || undefined;
  }

  private readRedirectOnSuccessUrlAttribute(): string | undefined {
    const raw =
      this.getAttribute('redirect-on-success-url') ??
      this.getAttribute('redirectonsuccessurl') ??
      this.getAttribute('redirect_on_success_url');
    const trimmed = raw?.trim();
    return trimmed || undefined;
  }

  private resolveSuccessRedirectUrl(): string | undefined {
    const redirect =
      this.readRedirectOnSuccessUrlAttribute() ??
      this.webformConfig.redirectOnSuccessUrl;
    return redirect || undefined;
  }

  private navigateToRedirect(url: string): void {
    const target = url.startsWith('https://') ? url : `https://${url}`;
    window.location.replace(target);
  }

  private getRuntimeConfig(): VobWebformRuntimeConfig {
    this.readRuntimeAttributes();
    return {
      apiBase: this.apiBase,
      clientId: this.clientId,
      facilityId: this.facilityId,
      authToken: this.apiToken,
      sendTo: this.readSendToAttribute(),
      emailTemplateId: this.readEmailTemplateIdAttribute(),
      redirectOnSuccessUrl: this.readRedirectOnSuccessUrlAttribute(),
      queue: this.readQueueAttribute(),
      webformAttributes: this.readPopulatedEmbedAttributes(),
      theme: { ...this.theme },
    };
  }

  private readThemeFromAttributes(): void {
    this.readRuntimeAttributes();

    const attrMap: Record<string, keyof VobWebformThemeAttributes> = {
      'primary-color': 'primaryColor',
      'background-color': 'backgroundColor',
      'text-color': 'textColor',
      'placeholder-color': 'placeholderColor',
      'input-color': 'inputColor',
      'input-background-color': 'inputBackgroundColor',
      'element-border-color': 'elementBorderColor',
    };

    Object.entries(attrMap).forEach(([attr, key]) => {
      const value = this.getAttribute(attr);
      if (value) {
        this.theme[key] = value;
      }
    });
  }

  private applyThemeToHost(): void {
    (Object.keys(THEME_CSS_VARS) as Array<keyof VobWebformThemeAttributes>).forEach(
      (key) => {
        this.style.setProperty(THEME_CSS_VARS[key], this.theme[key]);
      }
    );
  }

  private applyConfigTheme(config: VobWebformConfig): void {
    if (config.primaryColor) {
      this.theme.primaryColor = config.primaryColor;
    }
    if (config.backgroundColor) {
      this.theme.backgroundColor = config.backgroundColor;
    }
    if (config.placeholderColor) {
      this.theme.placeholderColor = config.placeholderColor;
    }
    if (config.inputColor) {
      this.theme.inputColor = config.inputColor;
    }
    if (config.inputBackgroundColor) {
      this.theme.inputBackgroundColor = config.inputBackgroundColor;
    }
    if (config.elementBorderColor) {
      this.theme.elementBorderColor = config.elementBorderColor;
    }
    this.applyThemeToHost();
  }

  private async loadConfig(): Promise<void> {
    if (!this.clientId || !this.facilityId) {
      this.loading = false;
      this.result = {
        success: false,
        validation: false,
        message: 'Missing client-id or facility-id attribute',
      };
      this.render();
      return;
    }

    this.loading = true;
    this.result = undefined;
    this.render();

    try {
      this.webformConfig = await loadWebformConfig(this.getRuntimeConfig());
      this.applyConfigTheme(this.webformConfig);
      this.loading = false;
    } catch (error) {
      this.loading = false;
      this.result = {
        success: false,
        validation: false,
        message:
          error instanceof Error
            ? error.message
            : 'Failed to load insurance form',
      };
    }
    this.render();
  }

  private onFieldInput(key: VobWebformFieldKey, value: string): void {
    this.client[key] = value;
  }

  private filterPayors(query: string): void {
    this.payorResults =
      query.length > 2
        ? this.webformConfig.payors.filter((payor) =>
            payor.payorName.toLowerCase().includes(query.toLowerCase())
          )
        : [];
    this.updatePayorResultsUI();
  }

  private updatePayorResultsUI(): void {
    if (!this.shadowRoot) {
      return;
    }

    const resultsContainer =
      this.shadowRoot.querySelector<HTMLElement>('.autocomplete .results');
    const list = resultsContainer?.querySelector('ul');
    if (!resultsContainer || !list) {
      return;
    }

    resultsContainer.classList.toggle('visible', this.payorResults.length > 0);
    list.innerHTML = this.payorResults
      .map(
        (payor, index) =>
          `<li data-payor-index="${index}">${escapeHtml(payor.payorName)}</li>`
      )
      .join('');

    list.querySelectorAll('[data-payor-index]').forEach((node) => {
      node.addEventListener('click', () => {
        const index = Number((node as HTMLElement).dataset.payorIndex);
        const payor = this.payorResults[index];
        if (payor) {
          this.selectPayor(payor);
        }
      });
    });
  }

  private selectPayor(payor: VobWebformPayor): void {
    this.client.payor_name = payor.payorName;
    this.client.payor_id = payor.payorId;
    if (payor.payorPlanId) {
      this.client.payor_plan_id = String(payor.payorPlanId);
    }
    this.payorResults = [];

    const payorLookup =
      this.shadowRoot?.querySelector<HTMLInputElement>('#payor_lookup');
    if (payorLookup) {
      payorLookup.value = payor.payorName;
    }

    this.updatePayorResultsUI();
  }

  private clearResult(): void {
    this.result = undefined;
    this.render();
  }

  private async onSubmit(event: Event): Promise<void> {
    event.preventDefault();
    this.loading = true;
    this.result = undefined;
    this.render();

    const campaign = collectUtmParams();
    if (Object.keys(campaign).length > 0) {
      this.client.campaign = campaign;
    }
    this.client.webform_origin = window.location.href;

    try {
      this.result = await submitVobWebform(
        this.getRuntimeConfig(),
        this.client
      );

      const redirect = this.resolveSuccessRedirectUrl();
      if (!this.result.validation && this.result.success && redirect) {
        this.navigateToRedirect(redirect);
      }
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private renderField(
    label: string,
    key: VobWebformFieldKey,
    options: { type?: string; required?: boolean } = {}
  ): string {
    const { type = 'text', required = false } = options;
    const value = escapeHtml(String(this.client[key] ?? ''));
    return `
      <div class="form-item">
        <label for="${key}">
          ${escapeHtml(label)}
          ${required ? '<span class="required">*</span>' : ''}
        </label>
        <input
          id="${key}"
          name="${key}"
          type="${type}"
          placeholder="${escapeHtml(label)}"
          value="${value}"
          ${required ? 'required' : ''}
          autocomplete="off"
        />
      </div>
    `;
  }

  private renderInsuranceLookup(): string {
    const payorName = escapeHtml(String(this.client.payor_name ?? ''));
    const visible = this.payorResults.length > 0 ? 'visible' : '';
    const items = this.payorResults
      .map(
        (payor, index) =>
          `<li data-payor-index="${index}">${escapeHtml(payor.payorName)}</li>`
      )
      .join('');

    return `
      <div class="form-item">
        <div class="autocomplete">
          <label for="payor_lookup">Look up your insurance company</label>
          <input
            id="payor_lookup"
            name="payor_lookup"
            type="text"
            placeholder="Type to search"
            value="${payorName}"
            required
            autocomplete="new-password"
          />
          <div class="results ${visible}">
            <ul>${items}</ul>
          </div>
        </div>
      </div>
    `;
  }

  private renderErrorBanner(): string {
    if (!this.result || this.result.success) {
      return '';
    }

    return `
      <div class="form-error-banner" role="alert">
        <p class="form-error-message">${escapeHtml(this.result.message)}</p>
        <button
          type="button"
          class="form-error-dismiss"
          data-action="dismiss-error"
          aria-label="Dismiss error"
        >×</button>
      </div>
    `;
  }

  private renderSuccessResult(): string {
    if (!this.result?.success) {
      return '';
    }

    const successIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>`;

    return `
      <div class="result result-success">
        <div class="result-body">
          <div class="success">${successIcon}</div>
          <p class="message success">${escapeHtml(this.result.message)}</p>
        </div>
      </div>
    `;
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const loadingOverlay = this.loading
      ? `<div class="loading-overlay" aria-busy="true"><div class="spinner"></div></div>`
      : '';

    const submitLabel = this.readQueueAttribute() ? 'Submit' : 'Check Insurance';

    this.shadowRoot.innerHTML = `
      <style>${WEBFORM_STYLES}</style>
      <div class="webform-element">
        ${this.renderErrorBanner()}
        <form novalidate>
          ${this.renderField('First Name', 'first_name', { required: true })}
          ${this.renderField('Last Name', 'last_name', { required: true })}
          ${this.renderField('Date of Birth', 'date_of_birth', {
            type: 'date',
            required: true,
          })}
          ${this.renderField('Email', 'email', { type: 'email' })}
          ${this.renderField('Phone', 'phone', { type: 'tel', required: true })}
          ${this.renderInsuranceLookup()}
          ${this.renderField('Member ID', 'member_id', { required: true })}
          <div class="form-item">
            <label for="webform_notes">Additional Notes</label>
            <textarea
              id="webform_notes"
              name="webform_notes"
              placeholder="Write any additional notes you wish us to know"
            >${escapeHtml(String(this.client.webform_notes ?? ''))}</textarea>
          </div>
          <div class="form-item">
            <button type="submit">${escapeHtml(submitLabel)}</button>
          </div>
        </form>
        ${this.renderSuccessResult()}
        ${loadingOverlay}
      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    if (!this.shadowRoot) {
      return;
    }

    const form = this.shadowRoot.querySelector('form');
    form?.addEventListener('submit', (e) => void this.onSubmit(e));

    const fields: Array<{ id: VobWebformFieldKey; key: VobWebformFieldKey }> = [
      { id: 'first_name', key: 'first_name' },
      { id: 'last_name', key: 'last_name' },
      { id: 'date_of_birth', key: 'date_of_birth' },
      { id: 'email', key: 'email' },
      { id: 'phone', key: 'phone' },
      { id: 'member_id', key: 'member_id' },
    ];

    fields.forEach(({ id, key }) => {
      const input = this.shadowRoot?.querySelector<HTMLInputElement>(`#${id}`);
      input?.addEventListener('input', () => {
        this.onFieldInput(key, input.value);
      });
    });

    const notes = this.shadowRoot.querySelector<HTMLTextAreaElement>('#webform_notes');
    notes?.addEventListener('input', () => {
      this.onFieldInput('webform_notes', notes.value);
    });

    const payorLookup =
      this.shadowRoot.querySelector<HTMLInputElement>('#payor_lookup');
    payorLookup?.addEventListener('input', () => {
      this.client.payor_name = payorLookup.value;
      this.client.payor_id = undefined;
      this.client.payor_plan_id = undefined;
      this.filterPayors(payorLookup.value);
    });

    this.updatePayorResultsUI();

    this.shadowRoot
      .querySelector('[data-action="dismiss-error"]')
      ?.addEventListener('click', () => this.clearResult());
  }
}
